import {createApi} from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "../../../../store/axiosBaseQuery";
import {SymbolCodepoints} from "react-material-symbols";

export enum MessageRoleType {
  USER = "user",
  ASSISTANT = "assistant",
}
function getMessageRoleType(role: string) {
  if (role === "user") return MessageRoleType.USER;
  if (role === "assistant") return MessageRoleType.ASSISTANT;
  throw new Error("Invalid message role");
}

/**
 * FollowUpPrompt interface
 * @property query - The query of the follow up prompt
 * @property queryId - The id of the follow up prompt
 */
export interface IFollowUpPrompt {
  query: string;
  queryId: string | null;
}
/**
 * Message interface
 * @property id - The id of the message
 * @property role - The role of the message
 * @property value - The value of the message
 * @property timestamp - The timestamp of the message
 */
export interface IMessage {
  id: string;
  role: MessageRoleType;
  value: string;
  timestamp: Date;
  followUpPrompts?: IFollowUpPrompt[];
}

interface IChatSession {
  messages: IMessage[];
}

interface ISuggestedPrompts {
  id: string;
  leadingIcon: SymbolCodepoints;
  title: string;
  subtitle: string;
  value: string;
}

interface ISuggestedPromptsResponse {
  suggestedPrompts: ISuggestedPrompts[];
}

export interface AssistantRequest {
  surfaceId: string;
  surfaceType: string;
  context: string;
  message: string;
  messageId?: string;
}

/**
 * AssistantResponse interface
 * @property messageId - The id of the message
 * @property status - The status of the message
 * @property response - The response of the message
 * @property completed - Whether the message is completed
 */
export interface AssistantResponse {
  messageId: string;
  status: string;
  response?: string;
  completed: boolean;
  followUpPrompts?: IFollowUpPrompt[];
}

const getHeaders = (surfaceId: string, surfaceType: string) => ({
  "X-Page-Type": surfaceType,
  "X-Page-Id": surfaceId,
});

export const aiAssistantApiSlice = createApi({
  reducerPath: "aiAssistantApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["NewMessage"],
  endpoints: builder => ({
    sendMessage: builder.mutation<AssistantResponse, AssistantRequest>({
      query: request => ({
        url: "/core/chatbot/ask/",
        method: "POST",
        headers: getHeaders(request.surfaceId, request.surfaceType),
        data: {
          context: request.context,
          query: request.message,
          chat_faq_reference_id: request.messageId ?? null,
        },
      }),
      transformResponse: (response: {message_id: string}) => {
        return {
          messageId: response.message_id,
          status: "Generating response...",
          response: undefined,
          completed: false,
        };
      },
      invalidatesTags: ["NewMessage"],
    }),

    getResponse: builder.query<AssistantResponse, string>({
      query: messageId => ({
        url: `/core/chatbot/response/`,
        method: "GET",
        params: {
          message_id: messageId,
        },
      }),
      transformResponse: (response: {
        message_id?: string;
        response?: string;
        status?: string;
        is_completed?: boolean;
        follow_up_questions?: {
          query: string;
          chat_faq_reference_id: string | null;
        }[];
      }) => {
        return {
          messageId: response.message_id ?? "",
          status: response.status ?? "Generating response...",
          response: response.response,
          completed: response.is_completed ?? false,
          followUpPrompts: response.follow_up_questions?.map(question => ({
            query: question.query,
            queryId: question.chat_faq_reference_id ?? null,
          })),
        };
      },
    }),

    getChatHistory: builder.query<
      IChatSession,
      {surfaceId: string; surfaceType: string}
    >({
      query: ({surfaceId, surfaceType}) => ({
        url: `/core/chatbot/history/`,
        headers: getHeaders(surfaceId, surfaceType),
      }),
      transformResponse: (response: {
        messages?: {
          role: string;
          content: string;
          timestamp: string;
          follow_up_questions?: {
            query: string;
            chat_faq_reference_id: string | null;
          }[];
        }[];
      }) => {
        return {
          messages:
            response.messages
              ?.map((message, idx) => ({
                id: idx.toString(),
                role: getMessageRoleType(message.role),
                value: message.content,
                timestamp: new Date(message.timestamp),
                followUpPrompts: message.follow_up_questions?.map(question => ({
                  query: question.query,
                  queryId: question.chat_faq_reference_id ?? null,
                })),
              }))
              .reverse() || [],
        };
      },
    }),

    updateChatHistory: builder.mutation<
      void,
      {surfaceId: string; surfaceType: string; message: IMessage}
    >({
      queryFn: async ({surfaceId, surfaceType, message}, api) => {
        api.dispatch(
          aiAssistantApiSlice.util.updateQueryData(
            "getChatHistory",
            {surfaceId, surfaceType},
            draft => {
              if (!draft.messages) {
                draft.messages = [];
              }
              draft.messages.push(message);
            },
          ),
        );
        return {data: undefined};
      },
    }),
  }),
});

export const {
  useSendMessageMutation,
  useGetResponseQuery,
  useGetChatHistoryQuery,
  useUpdateChatHistoryMutation,
} = aiAssistantApiSlice;
